import { Directive } from '../../../types';
import { PolicyConfigFormValues } from '../../PolicyConfigurationFormSection';

export interface KubernetesSecurityFormValues
  extends PolicyConfigFormValues,
    KubernetesSecurityTypeSpecificData {}

/**
 * Form data for Kubernetes Security Policy
 * These are the values displayed and edited in the form (UI representation)
 */
export interface KubernetesSecurityTypeSpecificData {
  // Security section
  /** Restrict access to the default namespace */
  restrictDefaultNamespace: boolean;
  /** Restrict secret contents access for non-admins (UI only) */
  restrictSecrets: boolean;

  // Pod security constraints
  enablePodSecurityConstraints: boolean;
  restrictPrivilegedContainers: boolean;
  restrictHostNamespace: boolean;
  restrictHostNetworkingPorts: boolean;
  hostNetwork: boolean;
  hostPortMin: number;
  hostPortMax: number;
  restrictVolumeTypes: boolean;
  allowedVolumeTypes: string[];
  restrictHostFilesystemPaths: boolean;
  allowedHostPaths: AllowedHostPath[];
  restrictDrivers: boolean;
  allowedFlexVolumeDrivers: string[];
  requireReadOnlyRootFilesystem: boolean;
  restrictUserAndGroupIds: boolean;
  runAsUserType: string;
  runAsUserRanges: IdRange[];
  runAsGroupType: string;
  runAsGroupRanges: IdRange[];
  supplementalGroupsType: string;
  supplementalGroupsRanges: IdRange[];
  fsGroupType: string;
  fsGroupRanges: IdRange[];
  restrictEscalationToRootPrivileges: boolean;
  restrictLinuxCapabilities: boolean;
  allowedCapabilities: string[];
  requiredDropCapabilities: string[];
  defaultAddCapabilities: string[];
  restrictSeLinuxSecurityContext: boolean;
  seLinuxOptions: SeLinuxOption[];
  restrictProcMountTypes: boolean;
  procMountType: string;
  restrictAppArmorProfiles: boolean;
  allowedAppArmorProfiles: string[];
  restrictSeccompProfiles: boolean;
  allowedSeccompProfiles: string[];
  restrictSysctlProfiles: boolean;
  forbiddenSysctls: string[];
}

/**
 * Stored/API payload for Kubernetes Security Policy
 * This matches the actual backend API structure with nested objects
 */
export interface KubernetesSecurityPolicyPayload {
  restrictDefaultNamespace: Directive<boolean>;
  restrictSecrets: Directive<boolean>;
  enablePodSecurityConstraints: Directive<boolean>;
  privilegedContainers: Directive<{ enabled: boolean }>;
  hostNamespaces: Directive<{ enabled: boolean }>;
  hostPorts: Directive<{
    enabled: boolean;
    hostNetwork: boolean;
    min: number;
    max: number;
  }>;
  volumeTypes: Directive<{
    enabled: boolean;
    allowedTypes: string[];
  }>;
  hostFilesystem: Directive<{
    enabled: boolean;
    allowedPaths: AllowedHostPath[];
  }>;
  allowFlexVolumes: Directive<{
    enabled: boolean;
    allowedVolumes: string[];
  }>;
  readOnlyRootFileSystem: Directive<{ enabled: boolean }>;
  users: Directive<{
    enabled: boolean;
    runAsUser: {
      type: string;
      idrange: IdRange[];
    };
    runAsGroup: {
      type: string;
      idrange: IdRange[];
    };
    supplementalGroups: {
      type: string;
      idrange: IdRange[];
    };
    fsGroups: {
      type: string;
      idrange: IdRange[];
    };
  }>;
  allowPrivilegeEscalation: Directive<{ enabled: boolean }>;
  capabilities: Directive<{
    enabled: boolean;
    allowedCapabilities: string[];
    requiredDropCapabilities: string[];
  }>;
  selinux: Directive<{
    enabled: boolean;
    allowedCapabilities: SeLinuxOption[];
  }>;
  allowProcMount: Directive<{
    enabled: boolean;
    procMountType: string;
  }>;
  appArmor: Directive<{
    enabled: boolean;
    AppArmorType: string[];
  }>;
  secComp: Directive<{
    enabled: boolean;
    secCompType: string[];
  }>;
  forbiddenSysctlsList: Directive<{
    enabled: boolean;
    requiredDropCapabilities: string[];
  }>;
}

/**
 * ID range for Kubernetes user/group configuration
 */
export interface IdRange {
  min: number;
  max: number;
}

/**
 * Host path configuration for Kubernetes
 */
export interface AllowedHostPath {
  pathPrefix: string;
  readonly: boolean;
}

/**
 * SELinux option configuration
 */
export interface SeLinuxOption {
  level: string;
  role: string;
  type: string;
  user: string;
}

/**
 * Default initial values for Kubernetes Security Policy type-specific fields (create mode)
 */
export const KUBERNETES_SECURITY_INITIAL_VALUES: KubernetesSecurityTypeSpecificData =
  {
    restrictDefaultNamespace: false,
    restrictSecrets: false,
    enablePodSecurityConstraints: false,
    restrictPrivilegedContainers: false,
    restrictHostNamespace: false,
    restrictHostNetworkingPorts: false,
    hostNetwork: false,
    hostPortMin: 0,
    hostPortMax: 0,
    restrictVolumeTypes: false,
    allowedVolumeTypes: [],
    restrictHostFilesystemPaths: false,
    allowedHostPaths: [],
    restrictDrivers: false,
    allowedFlexVolumeDrivers: [],
    requireReadOnlyRootFilesystem: false,
    restrictUserAndGroupIds: false,
    runAsUserType: 'RunAsAny',
    runAsUserRanges: [],
    runAsGroupType: 'RunAsAny',
    runAsGroupRanges: [],
    supplementalGroupsType: 'RunAsAny',
    supplementalGroupsRanges: [],
    fsGroupType: 'RunAsAny',
    fsGroupRanges: [],
    restrictEscalationToRootPrivileges: false,
    restrictLinuxCapabilities: false,
    allowedCapabilities: [],
    requiredDropCapabilities: [],
    defaultAddCapabilities: [],
    restrictSeLinuxSecurityContext: false,
    seLinuxOptions: [],
    restrictProcMountTypes: false,
    procMountType: 'Default',
    restrictAppArmorProfiles: false,
    allowedAppArmorProfiles: [],
    restrictSeccompProfiles: false,
    allowedSeccompProfiles: [],
    restrictSysctlProfiles: false,
    forbiddenSysctls: [],
  };

/**
 * Default initial values for complete Kubernetes Security Policy form (create mode)
 */
export const KUBERNETES_SECURITY_FORM_INITIAL_VALUES: KubernetesSecurityFormValues =
  {
    name: '',
    environmentGroupIds: [],
    ...KUBERNETES_SECURITY_INITIAL_VALUES,
  };
